import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nx/angular';
import * as ShipmentTimelineEventsActions from './shipment-timeline-events.actions';
import { ShipmentTimelineEventsServiceBase } from './../shipment-timeline-events-service-base';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { loadShipment } from '@qwyk/shared-stores/shipment-shared-actions';

@Injectable()
export class ShipmentTimelineEventsEffects {
    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentTimelineEventsActions.init),
            fetch({
                run: action => {
                    // Your custom service 'load' logic goes here. For now just return a success action...
                    return this.service
                        .getShipmentTimelineEvents(action.shipment.id)
                        .pipe(
                            map(shipmentTimelineEvents =>
                                ShipmentTimelineEventsActions.loadShipmentTimelineEventsSuccess(
                                    { shipmentTimelineEvents }
                                )
                            )
                        );
                },

                onError: (action, error) => {
                    console.error(error);
                    return ShipmentTimelineEventsActions.loadShipmentTimelineEventsFailure(
                        { error }
                    );
                },
            })
        )
    );

    createShipmentTimelineEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentTimelineEventsActions.createShipmentTimelineEvent),
            switchMap(action =>
                this.service
                    .createShipmentTimelineEvent(
                        action.shipment.id,
                        action.payload
                    )
                    .pipe(
                        map(shipmentTimelineEvent =>
                            ShipmentTimelineEventsActions.createShipmentTimelineEventSuccess(
                                {
                                    shipmentTimelineEvent,
                                    shipmentId: action.shipment.id,
                                }
                            )
                        ),
                        catchError(error =>
                            of(
                                ShipmentTimelineEventsActions.createShipmentTimelineEventFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    updateShipmentTimelineEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentTimelineEventsActions.updateShipmentTimelineEvent),
            switchMap(action =>
                this.service
                    .updateShipmentTimelineEvent(
                        action.shipment.id,
                        action.shipmentTimelineEvent.id,
                        action.changes
                    )
                    .pipe(
                        map(shipmentTimelineEvent =>
                            ShipmentTimelineEventsActions.updateShipmentTimelineEventSuccess(
                                { shipmentTimelineEvent }
                            )
                        ),
                        catchError(error =>
                            of(
                                ShipmentTimelineEventsActions.updateShipmentTimelineEventFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    confirmShipmentTimelineEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentTimelineEventsActions.confirmShipmentTimelineEvent),
            switchMap(action =>
                this.service
                    .confirmShipmentTimelineEvent(
                        action.shipment.id,
                        action.shipmentTimelineEvent.id,
                        action.changes
                    )
                    .pipe(
                        map(shipmentTimelineEvent =>
                            ShipmentTimelineEventsActions.confirmShipmentTimelineEventSuccess(
                                {
                                    shipmentTimelineEvent,
                                    shipmentId: action.shipment.id,
                                }
                            )
                        ),
                        catchError(error =>
                            of(
                                ShipmentTimelineEventsActions.confirmShipmentTimelineEventFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    confirmShipmentTimelineEventSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ShipmentTimelineEventsActions.confirmShipmentTimelineEventSuccess,
                ShipmentTimelineEventsActions.createShipmentTimelineEventSuccess
            ),
            switchMap(action =>
                of(loadShipment({ shipmentId: action.shipmentId }))
            )
        )
    );

    genericError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ShipmentTimelineEventsActions.updateShipmentTimelineEventFailure,
                    ShipmentTimelineEventsActions.confirmShipmentTimelineEventFailure,
                    ShipmentTimelineEventsActions.createShipmentTimelineEventFailure
                ),
                map(error => {
                    this.toastr.error(
                        error.error.message ?? "We're sorry, please try again",
                        'Something went wrong'
                    );
                })
            ),
        { dispatch: false }
    );

    constructor(
        private actions$: Actions,
        private service: ShipmentTimelineEventsServiceBase,
        private toastr: ToastrService
    ) {}
}
